import { useLazyQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import {
  Backdrop,
  ModalContainer,
  CloseButton,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalContent,
} from 'components/FormModal';
import PhoneInput from 'components/PhoneInput';

// Close icon SVG
const CloseIcon = () => (
  <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
    <path d="M2.146 2.854a.5.5 0 1 1 .708-.708L8 7.293l5.146-5.147a.5.5 0 0 1 .708.708L8.707 8l5.147 5.146a.5.5 0 0 1-.708.708L8 8.707l-5.146 5.147a.5.5 0 0 1-.708-.708L7.293 8 2.146 2.854Z" />
  </svg>
);

const SEARCH_USERS = gql`
  query SearchUsers($query: String!) {
    searchUsers(query: $query) {
      id
      first_name
      last_name
      cellphone
      country_code
      calling_code
      email
    }
  }
`;

const InstructionText = styled.p`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary || '#94A3B8'
      : '#64748B'};
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const UserList = styled.div`
  margin-top: 20px;
  max-height: 320px;
  overflow-y: auto;

  /* Custom scrollbar for dark/light theme */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.05)'
        : 'rgba(0, 0, 0, 0.05)'};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255, 255, 255, 0.2)'
        : 'rgba(0, 0, 0, 0.2)'};
    border-radius: 4px;

    &:hover {
      background: ${(props) =>
        props.theme.name === 'dark'
          ? 'rgba(255, 255, 255, 0.3)'
          : 'rgba(0, 0, 0, 0.3)'};
    }
  }
`;

const UserCard = styled.div`
  padding: 16px;
  border: 1px solid
    ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.1)'
        : 'rgba(0,0,0,0.1)'};
  border-radius: 8px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) =>
      props.theme.name === 'dark'
        ? 'rgba(255,255,255,0.05)'
        : 'rgba(0,0,0,0.02)'};
    border-color: ${(props) => props.theme?.colors?.primary || '#FA4616'};
  }
`;

const UserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.primary || '#ffffff'
      : props.theme.colors?.blueBlack || '#000033'};
  margin-bottom: 4px;
`;

const UserPhone = styled.div`
  font-size: 14px;
  color: ${(props) =>
    props.theme.name === 'dark'
      ? props.theme.colors?.textGray?.secondary || '#94A3B8'
      : '#64748B'};
`;

const SelectUserModal = ({ isOpen, onClose }) => {
  const history = useHistory();
  const { pathname } = useLocation();
  const [users, setUsers] = useState([]);
  const [modalRoot, setModalRoot] = useState(null);

  // Get modal root on mount
  useEffect(() => {
    setModalRoot(document.getElementById('modal-root') || document.body);
  }, []);

  // Handle escape key and body scroll
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Use lazy query so it only runs when we call searchUsers
  const [searchUsers] = useLazyQuery(SEARCH_USERS, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      setUsers(data?.searchUsers || []);
    },
    onError: () => {
      setUsers([]);
    },
  });

  // Determine route prefix based on current path
  const isTrackAdmin = pathname.includes('/admin-track');
  const isEmployeeAdmin = pathname.includes('/admin-employee');
  const routePrefix = isTrackAdmin
    ? '/admin-track'
    : isEmployeeAdmin
    ? '/admin-employee'
    : '/admin';

  const handlePhoneChange = async (phone) => {
    if (phone && phone.length >= 10) {
      await searchUsers({ variables: { query: phone } });
    } else {
      setUsers([]);
    }
  };

  const handleUserSelect = (userId) => {
    onClose();
    setUsers([]); // Clear users when closing
    history.push(`${routePrefix}/customers/issue-credit/${userId}`);
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!isOpen || !modalRoot) return null;

  return ReactDOM.createPortal(
    <EnhancedThemeProvider>
      <Backdrop onClick={handleBackdropClick}>
        <ModalContainer
          role="dialog"
          aria-labelledby="select-user-modal-title"
          aria-modal="true"
          maxWidth="540px"
          onClick={(e) => e.stopPropagation()}
        >
          <CloseButton onClick={onClose} aria-label="Close modal">
            <CloseIcon />
          </CloseButton>

          <ModalHeader>
            <ModalTitle id="select-user-modal-title">
              Select Customer
            </ModalTitle>
            <ModalSubtitle>Search for a customer to issue credit</ModalSubtitle>
          </ModalHeader>

          <ModalContent>
            <InstructionText>
              Enter the customer's mobile number to look up their account.
            </InstructionText>

            <PhoneInput
              label="Customer Phone Number"
              onChange={handlePhoneChange}
              placeholder="Enter phone number"
            />

            {users.length > 0 && (
              <UserList>
                {users.map((user) => (
                  <UserCard
                    key={user.id}
                    onClick={() => handleUserSelect(user.id)}
                  >
                    <UserName>
                      {user.first_name} {user.last_name}
                    </UserName>
                    <UserPhone>{user.cellphone}</UserPhone>
                  </UserCard>
                ))}
              </UserList>
            )}
          </ModalContent>
        </ModalContainer>
      </Backdrop>
    </EnhancedThemeProvider>,
    modalRoot
  );
};

export default SelectUserModal;
